/*
 * Full-screen noise meter scene. Everything keys off the CSS custom property
 * --level (0..1, written by the component each animation frame):
 *   - the red "intense" wash fades in as the room gets louder
 *   - a vignette darkens the edges at high levels
 *   - the central meter fills, and a soft pulse grows
 * Calm = cool, gentle, dark-blue aurora. Loud = hot, red, darker.
 */

.overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  font-family: var(--token-font-ui, system-ui, sans-serif);
  color: #fff;
  /* Calm base — deep, soft blue aurora. */
  background: radial-gradient(125% 120% at 50% 28%, #1c3f63 0%, #0c1c2e 64%, #06101b 100%);
  --accent: #5fd0a8;
}

/* Red intensity layer — opacity rides the level. */
.intense {
  position: absolute;
  inset: 0;
  background: radial-gradient(125% 120% at 50% 46%, #ff6a44 0%, #c01a2c 52%, #5c0b16 100%);
  opacity: var(--level, 0);
  transition: opacity 90ms linear;
}

/* Edge darkening + overall dim when loud. */
.vignette {
  position: absolute;
  inset: 0;
  box-shadow: inset 0 0 38vmin rgba(0, 0, 0, 0.7);
  background: rgba(0, 0, 0, calc(var(--level, 0) * 0.22));
  opacity: calc(0.35 + var(--level, 0) * 0.65);
  transition:
    opacity 90ms linear,
    background 90ms linear;
}

/* ---- chrome ---- */

.topbar {
  position: relative;
  z-index: 2;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: clamp(16px, 3vh, 32px) clamp(20px, 4vw, 48px);
}

.title {
  font-family: var(--token-font-display, var(--token-font-ui));
  font-size: clamp(18px, 2.4vh, 28px);
  letter-spacing: 0.5px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  opacity: 0.92;
}

.close {
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-family: var(--token-font-ui);
  font-weight: 700;
  font-size: clamp(13px, 1.8vh, 16px);
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.close:hover {
  background: rgba(255, 255, 255, 0.26);
}

/* ---- "Time to Calm" goal bar (top-center) ---- */

.goalBar {
  position: relative;
  z-index: 2;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0 16px;
  min-height: 40px;
}

.goalToggle,
.goalChip {
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-family: var(--token-font-ui);
  font-weight: 700;
  font-size: clamp(12px, 1.7vh, 15px);
  padding: 7px 16px;
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.goalToggle {
  opacity: 0.85;
}

.goalToggle:hover,
.goalChip:hover {
  background: rgba(255, 255, 255, 0.28);
  opacity: 1;
}

.goalPicker,
.goalActive,
.goalAchieved {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 7px 10px 7px 16px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(10, 16, 24, 0.4);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.goalText {
  font-family: var(--token-font-ui);
  font-size: clamp(13px, 1.9vh, 16px);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.goalDot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;
}

.goalDotOn {
  background: #36d399;
  box-shadow: 0 0 10px rgba(54, 211, 153, 0.8);
  animation: calmPulse 1.6s ease-in-out infinite;
}

@keyframes calmPulse {
  0%,
  100% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
}

.goalProgress {
  width: clamp(80px, 16vw, 160px);
  height: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  overflow: hidden;
  flex-shrink: 0;
}

.goalProgressFill {
  display: block;
  height: 100%;
  background: linear-gradient(to right, #2fd27a, #8be36a);
  transition: width 250ms linear;
}

.goalStop {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}

.goalStop:hover {
  background: rgba(255, 255, 255, 0.24);
}

.goalAchieved {
  border-color: rgba(54, 211, 153, 0.6);
  background: rgba(38, 122, 78, 0.4);
  animation: goalPop 360ms ease-out;
}

.goalCelebrate {
  font-size: 22px;
  animation: goalPop 600ms ease-out;
}

@keyframes goalPop {
  0% {
    transform: scale(0.7);
  }
  60% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .goalDotOn,
  .goalAchieved,
  .goalCelebrate {
    animation: none;
  }
}

/* ---- calm streak (count-up) ---- */

.streak {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 7px 10px 7px 16px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(10, 16, 24, 0.4);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.streakBroke {
  border-color: rgba(255, 90, 80, 0.7);
  background: rgba(120, 30, 30, 0.5);
  animation: streakShake 0.4s ease;
}

@keyframes streakShake {
  0%,
  100% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-6px);
  }
  40% {
    transform: translateX(6px);
  }
  60% {
    transform: translateX(-4px);
  }
  80% {
    transform: translateX(4px);
  }
}

.streakTime {
  font-family: var(--token-font-ui);
  font-size: clamp(14px, 2.1vh, 18px);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.streakStars {
  font-size: clamp(13px, 1.9vh, 17px);
  letter-spacing: 1px;
}

.streakBest {
  font-family: var(--token-font-ui);
  font-size: clamp(11px, 1.5vh, 13px);
  opacity: 0.72;
}

/* Toast + reset message float ABOVE the streak pill (toward the top bar) so
 * they never cover the meter in the middle of the screen. */
.streakToast {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-family: var(--token-font-display, var(--token-font-ui));
  font-weight: 800;
  font-size: clamp(18px, 3.4vh, 34px);
  color: #ffe14d;
  text-shadow: 0 2px 16px rgba(0, 0, 0, 0.55);
  animation: goalPop 500ms ease-out;
  pointer-events: none;
}

.streakBrokeMsg {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-family: var(--token-font-ui);
  font-weight: 700;
  font-size: clamp(14px, 2.2vh, 20px);
  color: #ff8a7a;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.5);
  pointer-events: none;
}

/* Radial emoji burst at each milestone. */
.confetti {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 0;
  height: 0;
  pointer-events: none;
}

.confettiPiece {
  position: absolute;
  left: 0;
  top: 0;
  font-size: 20px;
  animation: confettiFly 900ms ease-out forwards;
}

@keyframes confettiFly {
  0% {
    opacity: 0;
    transform: rotate(calc(var(--i) / var(--n) * 360deg)) translateY(0) scale(0.4);
  }
  20% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: rotate(calc(var(--i) / var(--n) * 360deg)) translateY(-90px) scale(1.1);
  }
}

.noticeRetry {
  margin-top: 6px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-family: var(--token-font-ui);
  font-weight: 700;
  font-size: clamp(13px, 1.8vh, 16px);
  padding: 8px 18px;
  border-radius: 999px;
  cursor: pointer;
}

.noticeRetry:hover {
  background: rgba(255, 255, 255, 0.28);
}

@media (prefers-reduced-motion: reduce) {
  .streakBroke,
  .streakToast,
  .confettiPiece {
    animation: none;
  }
}

/* ---- center stage ---- */

.stage {
  position: relative;
  z-index: 2;
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(18px, 4vh, 40px);
  min-height: 0;
}

/* Soft glow that breathes + grows with the level, behind the meter. */
.pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 46vmin;
  height: 46vmin;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 68%);
  transform: translate(-50%, -50%) scale(calc(0.75 + var(--level, 0) * 0.9));
  opacity: calc(0.18 + var(--level, 0) * 0.55);
  filter: blur(6px);
  transition:
    transform 120ms ease-out,
    opacity 120ms linear;
  animation: breathe 3.2s ease-in-out infinite;
}

@keyframes breathe {
  0%,
  100% {
    filter: blur(6px) brightness(1);
  }
  50% {
    filter: blur(10px) brightness(1.12);
  }
}

/* The meter: a vertical tube that fills green → amber → red. */
.meter {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 14px;
  height: min(46vh, 52vmin);
}

.track {
  position: relative;
  width: clamp(56px, 9vmin, 92px);
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  background: rgba(0, 0, 0, 0.28);
  overflow: hidden;
  box-shadow:
    inset 0 2px 8px rgba(0, 0, 0, 0.5),
    0 8px 30px -8px rgba(0, 0, 0, 0.6);
}

.fill {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: calc(var(--level, 0) * 100%);
  background: linear-gradient(to top, #2fd27a 0%, #ffe14d 55%, #ff5a4d 100%);
  box-shadow: 0 0 24px rgba(255, 255, 255, 0.35);
  transition: height 80ms linear;
}

/* Bright marker line at the current level. */
.needle {
  position: absolute;
  left: -4px;
  right: -4px;
  height: 4px;
  bottom: calc(var(--level, 0) * 100%);
  background: #fff;
  border-radius: 2px;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.9);
  transition: bottom 80ms linear;
}

.zonesAxis {
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  font-size: clamp(16px, 3vh, 26px);
  opacity: 0.85;
}

.readout {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
  text-shadow: 0 3px 18px rgba(0, 0, 0, 0.55);
}

.emoji {
  font-size: clamp(40px, 9vh, 92px);
  line-height: 1;
}

.zoneLabel {
  font-family: var(--token-font-display, var(--token-font-ui));
  font-size: clamp(34px, 8vh, 84px);
  font-weight: 800;
  line-height: 1.02;
  color: var(--accent);
  text-shadow:
    0 0 24px color-mix(in oklab, var(--accent) 60%, transparent),
    0 3px 18px rgba(0, 0, 0, 0.55);
}

.blurb {
  font-size: clamp(15px, 2.4vh, 22px);
  opacity: 0.85;
}

/* Per-zone accent for the word + its glow. */
.zone_calm {
  --accent: #5fd0a8;
}
.zone_good {
  --accent: #ffd76a;
}
.zone_loud {
  --accent: #ff7a6b;
}

/* ---- notice (no mic / requesting / denied) ---- */

.notice {
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  text-align: center;
  padding: 0 12vw;
}

.noticeEmoji {
  font-size: clamp(44px, 9vh, 88px);
}

.notice p {
  font-size: clamp(16px, 2.6vh, 24px);
  max-width: 28ch;
  line-height: 1.4;
  opacity: 0.92;
}

/* ---- calibration ---- */

.calibrateBar {
  position: relative;
  z-index: 2;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0 clamp(16px, 4vw, 48px) clamp(14px, 3vh, 28px);
}

.calToggle {
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-family: var(--token-font-ui);
  font-size: clamp(12px, 1.7vh, 15px);
  font-weight: 600;
  padding: 7px 16px;
  border-radius: 999px;
  cursor: pointer;
  opacity: 0.78;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.calToggle:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.2);
}

.calibrate {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: min(680px, 92vw);
  padding: 12px 18px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(10, 16, 24, 0.4);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.calHint {
  font-family: var(--token-font-ui);
  font-size: clamp(12px, 1.7vh, 15px);
  opacity: 0.85;
  text-align: center;
}

.calRow {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.calEnd {
  font-family: var(--token-font-ui);
  font-size: clamp(11px, 1.5vh, 13px);
  opacity: 0.75;
  flex-shrink: 0;
}

.calSlider {
  flex: 1;
  min-width: 0;
  accent-color: var(--accent);
  cursor: pointer;
}

.calDone {
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  font-family: var(--token-font-ui);
  font-weight: 700;
  font-size: clamp(12px, 1.6vh, 14px);
  padding: 6px 16px;
  border-radius: 999px;
  cursor: pointer;
}

.calDone:hover {
  background: rgba(255, 255, 255, 0.3);
}

@media (prefers-reduced-motion: reduce) {
  .pulse {
    animation: none;
  }
  .intense,
  .vignette,
  .fill,
  .needle,
  .pulse {
    transition: none;
  }
}
